import React, {useState, useEffect} from 'react';
import {
  CheckCircleIcon,
  ExclamationIcon,
  ChevronRightIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/solid';
import cx from 'classnames';
import {Disclosure} from '@headlessui/react';

import {Tabs} from '../Tabs';
import {
  Table,
  THead,
  TBody,
  TD,
  TH,
} from '../Markdown';

const TEST_SKIPPED_MSG = 'This test was skipped due to an error in a previous test.';

export const ConsoleOutput = ({isRunning, results}) => (
  <div className="font-mono p-4 font-medium">
    {isRunning && <p className="italic text-gray-500">Executing code...</p>}

    {!isRunning && results?.error && <pre className="text-red-600 mb-4 bg-gray-50">{results.error}</pre>}

    {!isRunning && results?.output && !results?.output?.columns && (
      <pre className="text-gray-600 mb-0">{results.output}</pre>
    )}

    {/* Table-formatted output */}
    {!isRunning && results?.output?.columns && (
      <Table className="border border-gray-100">
        <THead>
          <tr>
            {results.output.columns?.map((col) => (
              <TH key={col}>{col}</TH>
            ))}
          </tr>
        </THead>
        <TBody>
          {results.output.values?.map((row, i) => (
          // eslint-disable-next-line react/no-array-index-key
            <tr key={i}>
              {row.map((val) => (
                <TD>
                  {typeof val === 'object' ? JSON.stringify(val) : val}
                </TD>
              ))}
            </tr>
          ))}
        </TBody>
      </Table>
    )}

    {!isRunning && results && <p className="mt-3 italic text-gray-500">Finished executing code</p>}
  </div>
);

const TestCaseItemOutput = ({label, value}) => (
  <div>
    {label && <span className="font-medium text-gray-600 tracking-tight">{label}</span>}
    <div className="font-mono font-medium">
      <pre className="py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-600 mb-0">
        {value}
      </pre>
    </div>
  </div>
);

const TestCaseItem = ({testCase, index}) => (
  <Disclosure>
    {({open}) => (
      <div className="flex flex-col">
        <Disclosure.Button className="flex flex-row items-center justify-start hover:bg-gray-50 transition duration-50 px-2 -mx-2 py-2 rounded-lg">
          <ChevronRightIcon
            width={20}
            height={20}
            className={`${open ? 'rotate-90' : 'rotate-0'} transform text-gray-400`}
            fill="currentColor"
          />
          <div>
            {testCase.passed && <CheckCircleIcon className="h-5 w-5 text-green-600 mr-1" />}
            {(!testCase.passed && testCase.ran) && <ExclamationIcon className="h-5 w-5 text-red-600 mr-1" />}
            {(!testCase.ran) && <QuestionMarkCircleIcon className="h-5 w-5 text-yellow-600 mr-1" />}
            <span className="font-medium text-sm text-gray-700">{`Test case ${index + 1}`}</span>
          </div>
        </Disclosure.Button>
        <Disclosure.Panel className="pl-6">
          <div className="font-semibold text-gray-700 mb-2 flex flex-col gap-y-2">
            {!testCase.ran && <span className="font-normal italic text-sm text-gray-500">{TEST_SKIPPED_MSG}</span>}
            <TestCaseItemOutput label="Input" value={testCase.input} />
            <TestCaseItemOutput label="Expected" value={testCase.output || 'null'} />
            {testCase.ran && <TestCaseItemOutput label="Actual" value={testCase.actual || ' '} />}
            {testCase.stdError && <TestCaseItemOutput label="Output" value={testCase.stdError.trim()} />}
            {testCase.stdOut && <TestCaseItemOutput label="Output" value={testCase.stdOut} />}
            {testCase.description && (
              <div>
                Description:
                {' '}
                <span className="font-normal">{testCase.description}</span>
              </div>
            )}
          </div>
        </Disclosure.Panel>
      </div>
    )}
  </Disclosure>
);

export const TestOutput = ({isTesting, results}) => {
  if (isTesting) {
    return (
      <p className="p-4 font-mono font-medium italic text-gray-500">
        Executing tests...
      </p>
    );
  }

  /*
  * Display individual test cases if available
  * Otherwise, display the boolean or variable test results below
  */
  if (results?.testCases) {
    return (
      <div className="p-4 pb-0 flex flex-col gap-y-2">
        <div>
          <span className="font-semibold">Run time: </span>
          <span className="text-sm">{`${results.time} ms`}</span>
          {results.timedOut && <span className="ml-2 font-bold text-red-600">(Timed out)</span>}
        </div>
        <div className="flex flex-col">
          {results.testCases.map((testCase, index) => (
            <TestCaseItem testCase={testCase} index={index} />
          ))}
        </div>
        {results.hasError && (
          <div className="font-mono p-4 font-medium">
            <pre className="text-red-600 mb-0">{results.error}</pre>
          </div>
        )}
      </div>
    );
  }

  // Boolean test results
  if (results && typeof results.passed === 'boolean') {
    return (
      <div className="p-4 pb-0">
        <span className={cx('inline-flex items-end text-sm font-medium', {
          'text-gray-800': results.passed,
          'text-red-600': !results.passed,
        })}
        >
          {results.passed
            ? <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            : <ExclamationIcon className="h-5 w-5 text-red-600 mr-2" />}
          {results.passed ? 'Correct results!' : 'Incorrect results'}
        </span>
      </div>
    );
  }

  // Variable number of passing/failing tests
  const testsPassed = results?.failed === 0 && results?.passed > 0;
  return results && (
    <div className="p-4 pb-0">
      <span className={cx('inline-flex items-end text-sm font-medium', {
        'text-gray-800': testsPassed,
        'text-red-600': !testsPassed,
      })}
      >
        {testsPassed
          ? <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
          : <ExclamationIcon className="h-5 w-5 text-red-600 mr-2" />}
        {`${results.passed} ${results.total > 0 ? `of ${results.total} tests` : 'tests'} passed`}
      </span>
      {results.hasError && (
        <div className="font-mono p-4 font-medium">
          <pre className="text-red-600 mb-0">{results.error}</pre>
        </div>
      )}
    </div>
  );
};

const tabsWithTests = [{key: 'output', label: 'Output'}, {key: 'tests', label: 'Test Results'}];
const tabsWithoutTests = [{key: 'output', label: 'Output'}];

export const Console = ({
  hasTests,
  isRunning,
  isTesting,
  consoleResults,
  testResults,
}) => {
  const [activeTab, setActiveTab] = useState('output');

  useEffect(() => {
    if (!hasTests) setActiveTab('output');
  }, [hasTests]);

  useEffect(() => {
    if (consoleResults) setActiveTab('output');
  }, [consoleResults]);

  useEffect(() => {
    if (testResults) setActiveTab('tests');
  }, [testResults]);

  return (
    <div className="h-full overflow-scroll">
      <Tabs
        tabs={hasTests ? tabsWithTests : tabsWithoutTests}
        active={activeTab}
        onClick={setActiveTab}
        className="-mt-2 pl-4 bg-white sticky top-0 z-10"
      />
      {activeTab === 'output' && (
        <ConsoleOutput isRunning={isRunning} results={consoleResults} />
      )}
      {activeTab === 'tests' && (
        <TestOutput isTesting={isTesting} results={testResults} />
      )}
    </div>
  );
};

export default Console;
